import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { GeolocationService } from '../../services/geolocation.service';

@Component({
  selector: 'app-location-map-dialog',
  templateUrl: './location-map-dialog.component.html',
  styleUrls: ['./location-map-dialog.component.scss'],
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    TooltipModule,
    ProgressSpinnerModule
  ]
})
export class LocationMapDialogComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() sessionId: string | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  mapImageUrl: string = '';
  mapViewMode: 'path' | 'latest' = 'path';
  loading: boolean = false;
  error: string = '';
  googleMapsUrl: string = '';

  constructor(private geolocationService: GeolocationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && this.sessionId) {
      this.loadMapData();
    }
  }

  async loadMapData() {
    if (!this.sessionId) return;

    this.loading = true;
    this.error = '';

    try {
      // Load map image based on current mode
      await this.loadMapImage();

      // Load Google Maps URL
      this.googleMapsUrl = await this.geolocationService.getMapUrl(this.sessionId);
    } catch (error: any) {
      console.error('Failed to load location data:', error);
      this.error = error?.message || 'Failed to load location data';
      this.mapImageUrl = '';
      this.googleMapsUrl = '';
    } finally {
      this.loading = false;
    }
  }

  async loadMapImage() {
    if (!this.sessionId) return;

    try {
      this.mapImageUrl = await this.geolocationService.getMapImage(
        this.sessionId,
        this.mapViewMode
      );
    } catch (error: any) {
      throw new Error('Failed to load map image: ' + (error?.message || 'Unknown error'));
    }
  }

  async toggleViewMode() {
    this.mapViewMode = this.mapViewMode === 'path' ? 'latest' : 'path';
    this.loading = true;
    this.error = '';

    try {
      await this.loadMapImage();
    } catch (error: any) {
      console.error('Failed to load map image:', error);
      this.error = error?.message || 'Failed to load map image';
      this.mapImageUrl = '';
    } finally {
      this.loading = false;
    }
  }

  openInGoogleMaps() {
    if (this.googleMapsUrl) {
      window.open(this.googleMapsUrl, '_blank');
    }
  }

  closeDialog() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  get viewModeLabel(): string {
    return this.mapViewMode === 'path' ? 'Path View' : 'Latest Location';
  }

  get toggleButtonLabel(): string {
    return this.mapViewMode === 'path' ? 'Show Latest Only' : 'Show Full Path';
  }
}
